import React, { useEffect, useRef, useState, useCallback } from 'react';

// Import useEffectEvent - available in React 19.2 but not in stable types yet
// @ts-expect-error - experimental API
const useEffectEvent = React.useEffectEvent as <T extends Function>(event: T) => T;

// Fallback for older React versions (should not happen, but for safety)
if (!useEffectEvent) {
  throw new Error('useEffectEvent is not available. Please ensure React 19.2+ is installed.');
}

interface SessionManagerOptions {
  inactivityTimeout: number; // Milliseconds before showing warning (5 minutes)
  warningDuration: number; // Milliseconds to show warning (30 seconds)
  onLogout: () => void;
}

export const useSessionManager = ({
  inactivityTimeout = 5 * 60 * 1000, // 5 minutes
  warningDuration = 30 * 1000, // 30 seconds
  onLogout
}: SessionManagerOptions) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [isConnected, setIsConnected] = useState(true);
  
  const inactivityTimerRef = useRef<number | undefined>(undefined);
  const warningTimerRef = useRef<number | undefined>(undefined);
  const countdownIntervalRef = useRef<number | undefined>(undefined);
  const lastKeepAliveRef = useRef(Date.now());
  const failedAttemptsRef = useRef(0);
  const hasInitializedRef = useRef(false);

  // Handle logout - using useEffectEvent to avoid unnecessary re-renders
  const handleLogout = useEffectEvent(async () => {
    // Clear all timers
    if (inactivityTimerRef.current) {clearTimeout(inactivityTimerRef.current);}
    if (warningTimerRef.current) {clearTimeout(warningTimerRef.current);}
    if (countdownIntervalRef.current) {clearInterval(countdownIntervalRef.current);}

    // Clear CSRF token
    const { ApiClient } = await import('../utils/apiClient');
    ApiClient.clearCsrfToken();

    // Call logout handler
    onLogout();
  });

  // Show warning modal - using useEffectEvent for handleLogout
  const showWarningModal = useEffectEvent(() => {
    setShowWarning(true);
    setCountdown(30);

    // Start countdown
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set logout timer
    warningTimerRef.current = window.setTimeout(() => {
      handleLogout();
    }, warningDuration);
  });

  // Keep-alive check - using useEffectEvent to avoid circular dependencies
  const performKeepAlive = useEffectEvent(async () => {
    try {
      // Import ApiClient dynamically to avoid circular dependency
      const { ApiClient } = await import('../utils/apiClient');

      const response = await ApiClient.postJSON('/api/session/keepalive', {}, {
        maxRetries: 3,
        timeout: 5000,
        onRetry: (attempt) => console.log(`Retrying keepalive... attempt ${attempt}`)
      });

      if (response.ok) {
        const data = await response.json();

        if (data.status === 'active') {
          // User has an active session
          setIsConnected(true);
          failedAttemptsRef.current = 0;
          lastKeepAliveRef.current = Date.now();
          hasInitializedRef.current = true;

          // Reset the inactivity timer after successful keepalive
          resetInactivityTimer();
        } else if (data.status === 'no-session') {
          // No session yet (user hasn't logged in)
          setIsConnected(true);
          resetInactivityTimer();
        }
      } else if (response.status === 401) {
        // Session expired - only logout if we had a session before
        if (hasInitializedRef.current) {
          handleLogout();
        }
      } else {
        throw new Error('Non-OK response');
      }
    } catch (error) {
      failedAttemptsRef.current++;

      // Allow up to 3 failed attempts before considering disconnected
      if (failedAttemptsRef.current >= 3) {
        setIsConnected(false);
        // Only show warning if we had a session
        if (hasInitializedRef.current) {
          showWarningModal();
        }
      }
    }
  });

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Cancel any existing warning
    if (showWarning) {
      setShowWarning(false);
      setCountdown(30);
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer that will check server when time is up
    inactivityTimerRef.current = window.setTimeout(() => {
      // First check if we're still connected
      performKeepAlive();
    }, inactivityTimeout);
  }, [showWarning, inactivityTimeout]); // No more circular dependency!

  // User activity - called when user dismisses warning
  const dismissWarning = useEffectEvent(() => {
    // Perform keepalive check to ensure we're still connected
    performKeepAlive();
  });

  // Handle visibility change (sleep/wake detection)
  const handleVisibilityChange = useEffectEvent(() => {
    if (!document.hidden) {
      // Page became visible - check if session is still valid
      performKeepAlive();
    }
  });

  // Handle online/offline events
  const handleOnline = useEffectEvent(() => {
    // Internet connection restored - check session
    performKeepAlive();
  });

  // Setup listeners
  useEffect(() => {
    // Listen for page visibility changes (more efficient than activity tracking)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for network status changes
    window.addEventListener('online', handleOnline);

    // Start the inactivity timer
    resetInactivityTimer();

    // Initial session check
    performKeepAlive();

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);

      if (inactivityTimerRef.current) {clearTimeout(inactivityTimerRef.current);}
      if (warningTimerRef.current) {clearTimeout(warningTimerRef.current);}
      if (countdownIntervalRef.current) {clearInterval(countdownIntervalRef.current);}
    };
    // Effect Events are not dependencies!
  }, [resetInactivityTimer]);

  // Reset timer when user interacts with the app (called from components)
  const recordActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  return {
    showWarning,
    countdown,
    isConnected,
    dismissWarning,
    recordActivity
  };
};